<template>
	<Row class="page-container-row">
		<Col
			:span="24"
			:offset="0"
			:xl="xlSize"
			:xxl="xxlSize"
		>
			<PageContainer>
				<div class="scrollable-x">
					<div :class="['bs-list-header', { narrow: smallScreen }]">
						<div class="bs-list-header-main">
							<TypographyTitle :level="2" class="bs-list-title">
								{{ $t('base-stations.bsListHeader') }}
							</TypographyTitle>
							<div v-if="isBaseStationChangeAllowed" class="station-actions">
								<Tooltip :trigger="['hover']" placement="bottom">
									<template v-if="!allowAddingNewBaseStations" #title>
										<div>{{ $t('base-stations.workingStationsLimitHint') }}</div>
										<div>{{ $t('base-stations.workingStationsLimitShort', { working: workingBaseStationsCount, max: maxBaseStationsCount }) }}</div>
									</template>
									<span :class="['bs-add-button-wrap', { 'is-disabled': !allowAddingNewBaseStations }]">
										<DropdownButton
											type="primary"
											:disabled="!allowAddingNewBaseStations"
											@click="onAddButtonClick"
										>
											<PlusCircleOutlined />{{ $t('base-stations.addButtonLabel') }}
											<template #overlay>
												<Menu>
													<MenuItem :disabled="!allowAddingNewBaseStations" @click="onStrImportButtonClick">
														<FileAddOutlined class="bs-add-menu-item-icon" />
														{{ $t('base-stations.strImportButtonLabel') }}
													</MenuItem>
													<MenuDivider v-if="maxBaseStationsCount != null" />
													<MenuItem
														v-if="maxBaseStationsCount != null"
														disabled
														class="bs-license-limit-menu-item"
													>
														<div class="bs-license-limit-menu-content">
															<div>{{ $t('base-stations.workingStationsLimitHint') }}</div>
															<div>{{ $t('base-stations.workingStationsLimitShort', { working: workingBaseStationsCount, max: maxBaseStationsCount }) }}</div>
														</div>
													</MenuItem>
												</Menu>
											</template>
										</DropdownButton>
									</span>
								</Tooltip>
								<Tooltip :trigger="['hover']" placement="bottom" :title="$t('base-stations.downloadJsonButtonLabel')">
									<Button @click.prevent="onDownloadJson">
										<DownloadOutlined />
									</Button>
								</Tooltip>
								<UploadButton
									:label="$t('base-stations.uploadJsonButtonLabel')"
									action="conf_bridge/upload/start/base_stations/"
									label-visibility="never"
									:onSuccess="handleUploadSuccess"
								/>
								<ConfirmImportModal
									v-model="confirmImportModalIsVisible"
									mode="base_stations"
									:importData="importData"
									:onSuccess="onImportSuccess"
								/>
							</div>
						</div>
						<div>
							<PaasListStatus />
						</div>
					</div>
					<TablePaginationToolbar
						ref="paginationToolbarRef"
						v-model:items="baseStations"
						:defaultPageSize="20"
					/>
					<Table
						class="base-stations-table"
						:columns="columns"
						:data-source="paginatedBaseStations"
						:pagination="false"
						:row-selection="rowSelection"
						:loading="isLoading"
					>
						<template #headerCell="{ column }">
							<template v-if="column.key === 'antDetails'">
								<Tooltip :trigger="['hover']" placement="top" :title="$t('base-stations.tableColumns.antDetailsTooltip')">
									{{ column.title }} <QuestionCircleOutlined />
								</Tooltip>
							</template>
						</template>
						<template #bodyCell="{ column, record }">
							<template v-if="column.key === 'status'">
								<Badge :status="record.statusData.badge" :text="$t('baseStationStatus.' + record.status)" /><br>
							</template>
							<template v-if="column.key === 'statusOnline'">
								<Badge :status="record.statusOnlineData.badge" :text="$t('baseStationStatus.' + record.status_online)" />
							</template>
							<template v-if="column.key === 'statusMerged'">
								<Badge :status="record.statusData.badge" :text="$t('baseStationStatus.' + record.status)" /><br>
								<Badge :status="record.statusOnlineData.badge" :text="$t('baseStationStatus.' + record.status_online)" /><br>
								<Badge
									v-if="['broken', 'no_data'].includes(record.status_online)"
									:status="record.statusLiveData.badge"
									:text="$t('baseStationStatus.' + record.status_live)"
								/>
								<div v-if="record.paasCoordsStale" class="paas-coords-stale-alert">
									<WarningOutlined />
									<span v-if="record.paasCoordsAgeDays == null">{{ $t('paas.listPanel.neverTooltip') }}</span>
									<span v-else>{{ $t('paas.listPanel.coordsStaleDays', record.paasCoordsAgeDays) }}</span>
								</div>
								<div v-if="record.paasProblem?.has_error" class="paas-coords-stale-alert paas-problem-error-alert">
									<Tooltip
										v-if="record.paasProblem.message"
										placement="right"
										overlay-class-name="paas-problem-tooltip"
									>
										<template #title>
											<TableErrorMessagesDisplay
												errorKey="paas.reviewModal.errorMessages.errorHeader"
												errorLevel="error"
												:messages="record.paasProblemErrorMessages"
											/>
										</template>
										<span class="paas-problem-alert-content">
											<WarningOutlined />
											<span>{{ $t('paas.listPanel.paasError') }}</span>
										</span>
									</Tooltip>
									<template v-else>
										<WarningOutlined />
										<span>{{ $t('paas.listPanel.paasError') }}</span>
									</template>
								</div>
								<div v-if="record.paasProblem?.warnings?.length" class="paas-coords-stale-alert">
									<Tooltip placement="right" overlay-class-name="paas-problem-tooltip">
										<template #title>
											<TableErrorMessagesDisplay
												errorKey="paas.reviewModal.errorMessages.warningHeader"
												errorLevel="warning"
												:messages="record.paasProblemWarningMessages"
											/>
										</template>
										<span class="paas-problem-alert-content">
											<WarningOutlined />
											<span>{{ $t('paas.listPanel.paasWarning') }}</span>
										</span>
									</Tooltip>
								</div>
							</template>
							<template v-else-if="column.key === 'mountpoints'">
								<router-link
									v-if="isBaseStationChangeAllowed"
									:to="{ name: 'base-stations-edit', params: { id: record.id } }"
									:data-testid="record.id === paginatedBaseStations[0]?.id ? 'first-displayed-base-station-edit' : undefined"
								>
									<MountpointDisplay :baseStation="record" />
								</router-link>
								<MountpointDisplay v-else :baseStation="record" />
							</template>
							<template v-if="column.key === 'antDetails'">
								<div>
									{{ $t('base-stations-edit.fields.antReferencePoint.options.' + record.ant_reference_point) }}
									/
									<TypographyText :type="record.ant ? null : 'secondary'">
										{{ record.ant || $t('common.emptyValue') }}
									</TypographyText>
								</div>
								<DetectableFieldDisplay
									:dbValue="record.ant_reference_point"
									:compareValue="record.ant_reference_point_detected"
									nullValue="NA"
									displayValuePrefix="base-stations-edit.fields.antReferencePoint.options"
									compareAlertKey="base-stations-edit.fields.antReferencePoint.conflict.streamMessage"
									dbLabelKey="base-stations-edit.fields.antReferencePoint.conflict.databaseValue"
									compareLabelKey="base-stations-edit.fields.antReferencePoint.conflict.streamValue"
								/>
								<DetectableFieldDisplay
									v-if="record.allow_ant_detection"
									:dbValue="record.ant"
									:compareValue="record.ant_detected"
									nullValue=""
									compareAlertKey="base-stations-edit.fields.ant.conflict.streamMessage"
									dbLabelKey="base-stations-edit.fields.ant.conflict.databaseValue"
									compareLabelKey="base-stations-edit.fields.ant.conflict.streamValue"
								/>
							</template>
							<template v-else-if="column.key === 'displayAddress'">
								<TypographyText code>{{ record.display_address }}</TypographyText>
							</template>
							<template v-else-if="column.key === 'actions'">
								<ChangeStatusActionBar v-if="isBaseStationChangeAllowed" :target="record" :store="baseStationsStore" mode="baseStation" />
							</template>
						</template>
					</Table>
				</div>
				<GroupOperationsPopover :baseStations="selectedBaseStations" :onApply="onApplyGroupOperation" />
			</PageContainer>
		</Col>
	</Row>
</template>

<script>
import { ref, computed } from 'vue';
import { TypographyTitle, Table, Button, Row, Col, Badge, Tooltip, TypographyText, Dropdown, Menu } from 'ant-design-vue';
import { DownloadOutlined, PlusCircleOutlined, FileAddOutlined, QuestionCircleOutlined, WarningOutlined } from '@ant-design/icons-vue';

import { useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import TranslationUtils from '@/i18n/TranslationUtils';

import { useUserStore } from '@/stores/user';
import { useBaseStationsStore } from '@/stores/baseStations';
import { useLicenseStore } from '@/stores/licenseStore';
import { usePaasStore } from '@/stores/paas';

import { useSmallScreen } from '@/utils/useSmallScreen';
import { addBaseStationStatusData } from '@/utils/baseStationsUtils';

import GroupOperationsPopover from '@/components/baseStations/GroupOperationsPopover.vue';
import DetectableFieldDisplay from '@/components/baseStations/DetectableFieldDisplay.vue';
import TableErrorMessagesDisplay from '@/components/baseStations/TableErrorMessagesDisplay';
import ChangeStatusActionBar from '@/components/baseStations/ChangeStatusActionBar.vue';
import MountpointDisplay from '@/components/baseStations/MountpointDisplay.vue';

import PaasListStatus from '@/components/paas/PaasListStatus.vue';
import { isPaasCoordsStale, findPaasProblemForMountpoint, formatPaasWarningTooltip } from '@/components/paas/paasListStatusUtils';

import ConfirmImportModal from '@/components/common/ConfirmImportModal.vue';
import PageContainer from '@/components/common/PageContainer.vue';
import TablePaginationToolbar from '@/components/common/TablePaginationToolbar.vue';
import UploadButton from '@/components/common/UploadButton.vue';


export default {
	components: {
		TypographyTitle, Button, Row, Col, Table, Badge, Tooltip, TypographyText,
		GroupOperationsPopover,
		PageContainer, ChangeStatusActionBar, ConfirmImportModal, MountpointDisplay, UploadButton, DetectableFieldDisplay, TableErrorMessagesDisplay, PaasListStatus, TablePaginationToolbar,
		PlusCircleOutlined, FileAddOutlined, DownloadOutlined, QuestionCircleOutlined, WarningOutlined,
		DropdownButton: Dropdown.Button,
		Menu,
		MenuItem: Menu.Item,
		MenuDivider: Menu.Divider,
	},
	setup() {
		const { t } = useI18n();

		const baseStationsStore = useBaseStationsStore();
		const licenseStore = useLicenseStore();
		const paasStore = usePaasStore();
		const router = useRouter();

		// import modal
		const confirmImportModalIsVisible = ref(false);
		const importData = ref({});
		const handleUploadSuccess = response => {
			confirmImportModalIsVisible.value = true;
			importData.value = response;
		}
		const onImportSuccess = () => {
			baseStationsStore.baseStationRefreshList();
		};

		// table
		const user = useUserStore();
		const isBaseStationChangeAllowed = computed(() => user.isBaseStationChangeAllowed);
		const columns = TranslationUtils.addTranslatedField(
			user.baseStationsPageColumns,
			'key', 'title',
			'base-stations.tableColumns',
		);
		const baseStations = computed(() => baseStationsStore.items.map(item => {
			const ageDays = 'paas_coords_age_days' in item ? item.paas_coords_age_days : null;
			const paasProblem = licenseStore.allowPaas
				? findPaasProblemForMountpoint(paasStore.taskProblems, item.mntpnt_int)
				: null;
			const paasProblemErrorMessages = paasProblem?.has_error && paasProblem.message
				? [{ key: t('paas.reviewModal.errorMessages.errorKey'), message: paasProblem.message }]
				: [];
			const paasProblemWarningMessages = paasProblem?.warnings?.length
				? paasProblem.warnings.map(warning => ({
					key: warning.filename,
					message: warning.message,
				}))
				: [];
			return {
				...addBaseStationStatusData(item),
				paasCoordsAgeDays: ageDays,
				paasCoordsStale: licenseStore.allowPaas && 'paas_coords_age_days' in item && isPaasCoordsStale(ageDays),
				paasProblem,
				paasProblemErrorMessages,
				paasProblemWarningMessages,
				paasProblemWarningTooltip: paasProblem?.warnings?.length
					? formatPaasWarningTooltip(paasProblem.warnings)
					: '',
			};
		}));
		const paginationToolbarRef = ref(null);

		const isLoading = computed(() => baseStationsStore.isLoading && baseStationsStore.items.length === 0);
		
		const selectedRowKeys = ref([]);
		const selectedBaseStations = computed(() => baseStations.value.filter(item => selectedRowKeys.value.includes(item.id)));
		const onSelect = keys => selectedRowKeys.value = keys;
		const onApplyGroupOperation = () => selectedRowKeys.value = [];
		const rowSelection = computed(() => user.allowBaseStationsPageFull ? {
			selectedRowKeys: selectedRowKeys.value,
			onChange: onSelect,
			preserveSelectedRowKeys: true,
			selections: [{
				key: 'none',
				text: t('controls.table.deselectAll'),
				onSelect: () => {
					selectedRowKeys.value = [];
				},
			}],
		} : null);

		// layout
		const { smallScreen } = useSmallScreen();
		const xlSize = computed(() => user.allowBaseStationsPageFull ? { span: 24, offset: 0 } : { span: 18, offset: 3 });
		const xxlSize = computed(() => user.allowBaseStationsPageFull ? { span: 24, offset: 0 } : { span: 12, offset: 6 });

		// add buttons
		const maxBaseStationsCount = computed(() => baseStationsStore.maxBaseStationsCount);
		const workingBaseStationsCount = computed(() => baseStationsStore.items.filter(item => ['working', 'updating'].includes(item.status)).length);
		const allowAddingNewBaseStations = computed(() => {
			return baseStationsStore.maxBaseStationsCount == null || baseStationsStore.maxBaseStationsCount > workingBaseStationsCount.value;
		});

		const onAddButtonClick = () => router.push({ name: 'add-base-station' });
		const onStrImportButtonClick = () => router.push({ name: 'str-import' });
		const onDownloadJson = () => Api.confBridgeDownloadJson();

		const paginatedBaseStations = computed(() => {
			return (paginationToolbarRef.value != null) ? paginationToolbarRef.value.paginatedItems : [];
		});
		return {
			baseStationsStore,
			confirmImportModalIsVisible,
			importData,
			handleUploadSuccess,
			onImportSuccess,

			paginationToolbarRef,
	
			columns,
			baseStations,
			paginatedBaseStations,
			isBaseStationChangeAllowed,
			isLoading,
			selectedRowKeys,
			rowSelection,
			selectedBaseStations,
			onSelect,
			onApplyGroupOperation,

			allowAddingNewBaseStations,
			maxBaseStationsCount,
			workingBaseStationsCount,

			onAddButtonClick,
			onStrImportButtonClick,
			onDownloadJson,

			smallScreen,
			xlSize,
			xxlSize,

			colorWarningDark: Consts.COLORS.warningDark,
			colorErrorDark: Consts.COLORS.errorDark,
		};
	},
}
</script>

<style>
.ant-upload {
	width: 100%;
	display: block;
}

.base-stations-table .ant-typography code {
	display: inline-block;
}
</style>

<style scoped>
.bs-list-header {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	align-items: start;
	margin-bottom: 16px;
}

.bs-list-header.narrow {
	grid-template-columns: 1fr;
}

.bs-list-header-main {
	display: flex;
	flex-direction: column;
	gap: 12px;
	align-items: flex-start;
	min-width: 0;
}

.bs-list-title {
	margin-bottom: 0;
}

.station-actions {
	display: flex;
	flex-wrap: nowrap;
	gap: 8px;
	align-items: center;
}

.bs-add-button-wrap {
	display: inline-block;
}

.bs-add-button-wrap.is-disabled {
	cursor: not-allowed;
}

.bs-list-header.narrow .station-actions {
	width: 100%;
}

.bs-list-header.narrow .station-actions :deep(.ant-dropdown-button) {
	flex: 1;
	min-width: 0;
}

.paas-coords-stale-alert {
	margin-top: 4px;
	line-height: 1.4;
	color: v-bind(colorWarningDark);
}

.paas-coords-stale-alert .anticon {
	margin-right: 4px;
}

.paas-coords-stale-alert.paas-problem-error-alert {
	color: v-bind(colorErrorDark);
}

.paas-problem-alert-content {
	display: inline-flex;
	align-items: center;
	gap: 4px;
}
</style>

<style>
.ant-tooltip.paas-problem-tooltip {
	max-width: none;
}

.paas-problem-tooltip .ant-tooltip-inner {
	width: max-content;
	max-width: none;
	white-space: pre;
}

.paas-problem-tooltip .table-error-display-alert-wrapper {
	width: max-content;
	max-width: none;
}

.paas-problem-tooltip .table-error-display-alert-value-message-content {
	white-space: pre;
	word-wrap: normal;
	overflow-wrap: normal;
}

.bs-add-menu-item-icon {
	margin-right: 8px;
}

:deep(.bs-license-limit-menu-item.ant-dropdown-menu-item-disabled) {
	cursor: default;
	white-space: normal;
	line-height: 1.4;
	height: auto;
	padding-top: 8px;
	padding-bottom: 8px;
}

:deep(.bs-license-limit-menu-item.ant-dropdown-menu-item-disabled .ant-dropdown-menu-title-content) {
	color: rgba(0, 0, 0, 0.88);
}

.bs-license-limit-menu-content {
	font-size: 12px;
}
</style>
